import React, { useEffect, useState, useContext } from "react";
import API from "../../api/api";
import { AuthContext } from "../../context/AuthContext";
import Navbar from "../../components/Navbar";
import { resolveAssetUrl } from "../../api/config";
import SearchableSelect from "../../components/common/SearchableSelect";
import Swal from "sweetalert2";
import { errorAlert, successAlert, warningAlert } from "../../utils/alert";

const formatAgeCategoryLabel = (value) => {
  const token = String(value || "").toLowerCase();
  if (token === "sub_junior") return "Sub-Junior";
  if (token === "junior") return "Junior";
  if (token === "senior") return "Senior";
  return token ? token : "Unknown";
};

const gameLabel = (game) => {
  const id = game?.id ?? game?.game_id;
  const label = String(
    game?.game_name ||
      game?.name ||
      game?.title ||
      game?.label ||
      game?.text ||
      game?.event_game ||
      game?.game ||
      (id ? `Game #${id}` : "")
  ).trim();
  return label || (id ? `Game #${id}` : "—");
};

const normalizeGameOptions = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const byId = new Map();

  list.forEach((row) => {
    const rawId = row?.id ?? row?.game_id ?? row?.value;
    const id = Number(rawId);
    if (!Number.isFinite(id) || id <= 0) return;

    const option = {
      ...row,
      id,
      game_id: id,
      game_name: gameLabel({ ...row, id }),
    };
    byId.set(String(id), option);
  });

  return Array.from(byId.values()).sort((a, b) =>
    gameLabel(a).localeCompare(gameLabel(b), undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );
};

const EventRegistrations = () => {
  const { role } = useContext(AuthContext);
  const roleLower = String(role || "").toLowerCase();
  const [selectedGameReg, setSelectedGameReg] = useState(null);
  const [availableGames, setAvailableGames] = useState([]); // games dropdown for the sport
  const [newGameForReg, setNewGameForReg] = useState("");
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState([]);
  const [filter, setFilter] = useState("All");
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [selectedEscort, setSelectedEscort] = useState(null); // 🚀 new
  const [previewImage, setPreviewImage] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [allPlayers, setAllPlayers] = useState([]);
  const [selectionBusy, setSelectionBusy] = useState(false);
  const [selectionForm, setSelectionForm] = useState({
    event_id: "",
    player_id: "",
    game_ids: [],
  });
  const [selectionGames, setSelectionGames] = useState([]);
  const [eventStatusFilter, setEventStatusFilter] = useState({
    Open: true,
    Closed: true,
  });
  const [escortForm, setEscortForm] = useState({
    escort_name: "",
    escort_email: "",
    escort_type: "",
    escort_photo: null,

    escort_name_2: "",
    escort_email_2: "",
    escort_type_2: "",
    escort_photo_2: null,
  });

  const statusLabel = (row) => {
    if (row.status !== "Pending") return row.status;
    const stage = (row.approval_stage || "District").toString().trim();
    return `Pending at ${stage}`;
  };

  const loadEvents = async () => {
    const res = await API.fetchAdminEvents();
    setEvents(res.data || res || []);
  };

  const loadPlayers = async () => {
    try {
      const res = await API.fetchAllPlayersFull();
      const list = res?.data || res || [];
      setAllPlayers(Array.isArray(list) ? list : []);
    } catch (_err) {
      setAllPlayers([]);
    }
  };

  const getImageUrl = (path) =>
    resolveAssetUrl(path);

  const loadData = async () => {
    try {
      const [regsRes, statsRes] = await Promise.all([
        API.fetchEventRegistrations(),
        API.fetchEventRegistrationStats(),
      ]);
      setRegistrations(regsRes.data || regsRes || []);
      setStats(statsRes.data || statsRes || []);
    } catch (err) {
      console.error("Error loading registration data:", err);
      errorAlert("Load failed", "Failed to load registration data.");
    }
  };

  useEffect(() => {
    loadData();
    loadEvents();
    loadPlayers();
  }, []);

  useEffect(() => {
    const handler = (e) => e.key === "Escape" && setSelectedEscort(null);
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    if (!id || updatingStatusId === id) return;
    setUpdatingStatusId(id);
    Swal.fire({
      title: "Updating...",
      text: `Setting registration to ${newStatus}`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    try {
      await API.updateRegistrationStatus(id, newStatus);
      Swal.close();
      Swal.fire({
        icon: "success",
        title: "Updated",
        text: `Registration ${newStatus.toLowerCase()} successfully.`,
        timer: 1400,
        showConfirmButton: false,
      });
      await loadData();
    } catch (err) {
      console.error("Error updating status:", err);
      const errorMsg = err?.message || "Error updating registration status.";
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Update failed",
        text: errorMsg,
      });
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const openGameChangeFor = async (gameObj, parentRow) => {
    // gameObj: { reg_id, game_id, game_name }  — from games_json parsed
    // parentRow: the r (player-event row) — we need sport_id to load all games for sport
    setSelectedGameReg({
      reg_id: gameObj.reg_id,
      current_game_id: Number(gameObj.game_id),
      current_game_name: gameLabel(gameObj),
      player_name: parentRow.player_name,
      sport_id: parentRow.sport_id,
      event_id: parentRow.event_id,
    });
    setNewGameForReg(String(gameObj.game_id || ""));
    // Prefer event-scoped games; fallback to sport-scoped list.
    let list = [];
    try {
      if (parentRow?.event_id) {
        const eventGamesRes = await API.get(`/admin/game-engine/events/${parentRow.event_id}/games`);
        list = Array.isArray(eventGamesRes?.data)
          ? eventGamesRes.data
          : (Array.isArray(eventGamesRes) ? eventGamesRes : []);
      }
      if (!Array.isArray(list) || !list.length) {
        const res = await API.fetchGamesBySport(parentRow.sport_id);
        list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      }
    } catch (_err) {
      try {
        const res = await API.fetchGamesBySport(parentRow.sport_id);
        list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      } catch (_fallbackErr) {
        list = [];
      }
    }

    setAvailableGames(normalizeGameOptions(list));
  };

  const saveChangedSingleGame = async () => {
    if (!selectedGameReg) return;
    if (!newGameForReg) {
      warningAlert("Game required", "Please select a game.");
      return;
    }
    try {
      // reg_id is the event_registrations.id of the specific game row
      await API.changeRegistrationGame(selectedGameReg.reg_id, Number(newGameForReg));
      successAlert("Game updated", "Game changed successfully.");
      setSelectedGameReg(null);
      setAvailableGames([]);
      setNewGameForReg("");
      await loadData(); // reload registrations + stats
    } catch (err) {
      console.error("Error changing game:", err);
      errorAlert("Update failed", "Error changing game.");
    }
  };
  // 🚀 Open escort modal
  const openEscortModal = (reg) => {
    setSelectedEscort(reg);

    setEscortForm({
      escort_name: reg.escort_name || "",
      escort_email: reg.escort_email || "",
      escort_type: reg.escort_type || "",
      escort_photo: null,

      escort_name_2: reg.escort_name_2 || "",
      escort_email_2: reg.escort_email_2 || "",
      escort_type_2: reg.escort_type_2 || "",
      escort_photo_2: null,
    });
  };

  // 🚀 Update escort details
  const handleEscortChange = (e) => {
    const { name, value, files } = e.target;
    setEscortForm({
      ...escortForm,
      [name]: files ? files[0] : value,
    });
  };

  const saveEscortDetails = async () => {
    if (!selectedEscort) return;

    try {
      const formData = new FormData();
      formData.append("_method", "PUT");

      formData.append("escort_name", escortForm.escort_name);
      formData.append("escort_email", escortForm.escort_email || "");
      formData.append("escort_type", escortForm.escort_type);
      if (escortForm.escort_photo) {
        formData.append("escort_photo", escortForm.escort_photo);
      }

      // second escort (optional)
      if (escortForm.escort_name_2) {
        formData.append("escort_name_2", escortForm.escort_name_2);
        formData.append("escort_email_2", escortForm.escort_email_2 || "");
        formData.append("escort_type_2", escortForm.escort_type_2);
      }

      if (escortForm.escort_photo_2) {
        formData.append("escort_photo_2", escortForm.escort_photo_2);
      }

      await API.post(
        `/admin/registrations/${selectedEscort.registration_id}/update-escort`,
        formData
      );

      successAlert("Escort updated", "Escort details updated successfully.");
      setSelectedEscort(null);
      await loadData();
    } catch (err) {
      console.error("Error updating escort:", err.response?.data || err.message);
      errorAlert("Update failed", "Error updating escort details.");
    }
  };

  const filteredRegs = registrations.filter((r) => {
    if (filter !== "All" && r.status !== filter) return false;

    if (selectedEvent && String(r.event_id) !== String(selectedEvent)) {
      return false;
    }

    if (
      !eventStatusFilter[r.event_registration_status || r.registration || "Open"]
    ) {
      return false;
    }

    return true;
  });

  const filteredStatusCounts = filteredRegs.reduce(
    (acc, row) => {
      const key = String(row.status || "Pending");
      if (key === "Approved") acc.Approved += 1;
      else if (key === "Rejected") acc.Rejected += 1;
      else acc.Pending += 1;
      return acc;
    },
    { Pending: 0, Approved: 0, Rejected: 0 }
  );

  const selectedSelectionEvent = events.find((ev) => String(ev.id) === String(selectionForm.event_id)) || null;
  const selectionEligibility = selectionGames.find((g) => g?.eligibility)?.eligibility || null;
  const higherLevelEvents = events.filter((ev) => {
    const level = String(ev.event_level || "").toLowerCase().trim();
    return ["state", "national", "international"].includes(level);
  });

  useEffect(() => {
    const loadSelectionGames = async () => {
      if (!selectedSelectionEvent?.sport_id) {
        setSelectionGames([]);
        setSelectionForm((prev) => ({ ...prev, game_ids: [] }));
        return;
      }
      try {
        const res = await API.fetchGamesBySport(
          selectedSelectionEvent.sport_id,
          selectionForm.player_id ? { player_id: Number(selectionForm.player_id) } : {}
        );
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setSelectionGames(normalizeGameOptions(list));
        setSelectionForm((prev) => ({ ...prev, game_ids: [] }));
      } catch (_err) {
        setSelectionGames([]);
        setSelectionForm((prev) => ({ ...prev, game_ids: [] }));
      }
    };

    loadSelectionGames();
  }, [selectedSelectionEvent?.id, selectedSelectionEvent?.sport_id, selectionForm.player_id]);

  const toggleSelectionGame = (game) => {
    const gameId = Number(game?.id || 0);
    const eligibility = game?.eligibility || null;
    if (eligibility?.known && !eligibility?.eligible) {
      warningAlert("Not eligible", eligibility?.reason || "Selected player is not eligible for this game.");
      return;
    }
    setSelectionForm((prev) => {
      const sid = String(gameId);
      const exists = prev.game_ids.includes(sid);
      return {
        ...prev,
        game_ids: exists
          ? prev.game_ids.filter((id) => id !== sid)
          : [...prev.game_ids, sid],
      };
    });
  };

  const submitSelection = async () => {
    if (!selectionForm.event_id || !selectionForm.player_id || !selectionForm.game_ids.length) {
      warningAlert("Missing fields", "Choose event, player, and at least one game.");
      return;
    }

    try {
      setSelectionBusy(true);
      await API.selectPlayerForEvent({
        event_id: Number(selectionForm.event_id),
        player_id: Number(selectionForm.player_id),
        game_ids: selectionForm.game_ids.map((id) => Number(id)),
      });
      successAlert("Player selected", "Player was added/forwarded for this event.");
      setSelectionForm({ event_id: "", player_id: "", game_ids: [] });
      setSelectionGames([]);
      await loadData();
    } catch (err) {
      errorAlert("Selection failed", err?.message || "Unable to select player for event.");
    } finally {
      setSelectionBusy(false);
    }
  };

  if (!["admin", "secretary", "district"].includes(roleLower)) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Unauthorized Access
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Approval Desk</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Event Registrations</h1>
              <p className="text-sm text-slate-600 mt-1">
                Control approvals, escort updates, nominations, and ID-card generation from one board.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Total</p>
                <p className="text-lg font-semibold text-slate-900">{registrations.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Filtered</p>
                <p className="text-lg font-semibold text-slate-900">{filteredRegs.length}</p>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-amber-700">Pending</p>
                <p className="text-lg font-semibold text-amber-800">{filteredStatusCounts.Pending}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Approved</p>
                <p className="text-lg font-semibold text-emerald-800">{filteredStatusCounts.Approved}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {stats.map((s) => (
            <div key={s.event_id} className="p-3 sm:p-4 border border-slate-200 rounded-2xl bg-white shadow-sm">
              <h3 className="font-semibold text-blue-700 text-base sm:text-lg mb-2">
                {s.event_title}
              </h3>
              <p>Pending: {s.pending || 0}</p>
              <p className="text-green-700">Approved: {s.approved || 0}</p>
              <p className="text-red-600">Rejected: {s.rejected || 0}</p>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex justify-end mb-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <SearchableSelect
            className="border p-2 rounded w-full max-w-[240px]"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </SearchableSelect>
        </div>
        
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow mb-4 flex flex-wrap gap-4 items-end">

          {/* Event selector */}
          <div>
            <label className="block text-sm font-medium mb-1">Event</label>
            <SearchableSelect
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="border p-2 rounded w-64"
            >
              <option value="">All Events</option>
              {events.map(ev => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </SearchableSelect>
          </div>

          {/* Open / Closed filter */}
          <div>
            <label className="block text-sm font-medium mb-1">Event Status</label>
            <div className="flex gap-3">
              {["Open", "Closed"].map(s => (
                <label key={s} className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={eventStatusFilter[s]}
                    onChange={() =>
                      setEventStatusFilter(prev => ({
                        ...prev,
                        [s]: !prev[s],
                      }))
                    }
                  />
                  {s}
                </label>
              ))}
            </div>
          </div>

          {/* Bulk ID cards */}
          <div className="ml-auto">
            <button
              disabled={!selectedEvent}
              onClick={async () => {
                if (!selectedEvent) return;
                const result = await Swal.fire({
                  icon: "question",
                  title: "Download all approved ID cards?",
                  text: "Download ALL approved ID cards for this event?",
                  showCancelButton: true,
                  confirmButtonText: "Download",
                });
                if (!result.isConfirmed) return;
                API.downloadAdminBulkIds(selectedEvent);
              }}
              className={`px-4 py-2 rounded text-white ${
                selectedEvent
                  ? "bg-indigo-600 hover:bg-indigo-700"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              Bulk ID Cards
            </button>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow mb-4">
          <h3 className="text-base font-semibold text-indigo-700 mb-2">
            Select Players for State/National Events
          </h3>
          <p className="text-xs text-gray-600 mb-3">
            Players cannot apply directly to higher-level events. District/Secretary/Admin can nominate here.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Higher-Level Event</label>
              <SearchableSelect
                value={selectionForm.event_id}
                onChange={(e) =>
                  setSelectionForm((prev) => ({
                    ...prev,
                    event_id: e.target.value,
                    game_ids: [],
                  }))
                }
                className="w-full border p-2 rounded"
              >
                <option value="">-- Select Event --</option>
                {higherLevelEvents.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title}
                  </option>
                ))}
              </SearchableSelect>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Player</label>
              <SearchableSelect
                value={selectionForm.player_id}
                onChange={(e) =>
                  setSelectionForm((prev) => ({ ...prev, player_id: e.target.value }))
                }
                className="w-full border p-2 rounded"
              >
                <option value="">-- Select Player --</option>
                {allPlayers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.district || "-"})
                  </option>
                ))}
              </SearchableSelect>
            </div>
            <div className="flex items-end">
              <button
                onClick={submitSelection}
                disabled={
                  selectionBusy ||
                  !selectionForm.event_id ||
                  !selectionForm.player_id ||
                  !selectionForm.game_ids.length
                }
                className="w-full px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-400"
              >
                {selectionBusy ? "Submitting..." : "Select / Forward Player"}
              </button>
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-sm font-medium mb-1">Games</label>
            {selectionEligibility ? (
              <p className="text-xs text-gray-600 mb-2">
                Category: {formatAgeCategoryLabel(selectionEligibility.player_age_category)} | Gender:{" "}
                {selectionEligibility.player_gender || "-"} | Class: {selectionEligibility.player_classification || "-"}
              </p>
            ) : null}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {selectionGames.map((g) => {
                const checked = selectionForm.game_ids.includes(String(g.id));
                return (
                  <label
                    key={g.id}
                    className={`flex items-start gap-2 border rounded px-2 py-1 text-sm ${
                      g?.eligibility?.known && !g?.eligibility?.eligible ? "bg-rose-50 border-rose-200" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={g?.eligibility?.known && !g?.eligibility?.eligible}
                      onChange={() => toggleSelectionGame(g)}
                    />
                    <span>
                      <span>{g.game_name}</span>
                      {g?.eligibility?.eligible_classes ? (
                        <div className="text-[11px] text-gray-500">
                          Eligible classes: {g.eligibility.eligible_classes}
                        </div>
                      ) : null}
                      {g?.eligibility?.known && !g?.eligibility?.eligible && g?.eligibility?.reason ? (
                        <div className="text-[11px] text-rose-700">{g.eligibility.reason}</div>
                      ) : null}
                    </span>
                  </label>
                );
              })}
              {!selectionGames.length ? (
                <span className="text-xs text-gray-500">Select a higher-level event to load games.</span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Table */}
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-3">
            <h3 className="text-lg font-semibold text-slate-900">Registration Queue</h3>
            <p className="text-sm text-slate-600">
              Review status, escorts, payment proofs, and event game mappings for each player.
            </p>
          </div>
        <div className="overflow-x-auto">
          <table style={{ verticalAlign: "middle" }} className="event-reg-table min-w-full border border-gray-300 rounded-lg text-xs sm:text-sm">
            <thead className="bg-blue-100 text-gray-700">
              <tr>
                <th className="p-2 border">Player</th>
                <th className="p-2 border">Event</th>
                <th className="p-2 border">Level</th>
                <th className="p-2 border">Sport</th>
                <th className="p-2 border">Game</th>
                <th className="p-2 border">Escort</th>
                <th className="p-2 border">Payment</th>
                <th className="p-2 border">Status</th>
                <th className="p-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRegs.map((r) => (
                <tr key={r.registration_id} className="text-center">
                  <td className="p-2 border font-medium">{r.player_name}</td>
                  <td className="p-2 border">{r.event_title}</td>
                  <td className="p-2 border capitalize">{String(r.event_level || "district")}</td>
                  <td className="p-2 border">{r.sport_name}</td>
                  <td className="p-2 border align-top">
                    {(() => {
                      try {
                        const gamesArr = (r.games_concat || "")
                          .split("||")
                          .filter(Boolean)
                          .map((item) => {
                            const [reg_id, game_id, ...nameParts] = item.split("::");
                            return {
                              reg_id: Number(reg_id),
                              game_id: Number(game_id),
                              game_name: nameParts.join("::"),
                            };
                          });

                        return (
                          <div className="space-y-0.5">
                            {gamesArr.length ? (
                              gamesArr.map((gObj) => (
                                <div
                                  key={`${gObj.reg_id}_${gObj.game_id}`}
                                  className="flex items-center justify-between gap-2"
                                >
                                  <span className="text-xs">{gameLabel(gObj)}</span>
                                  <button
                                    onClick={() => openGameChangeFor(gObj, r)}
                                    disabled={!Number(gObj.game_id)}
                                    className="text-[11px] px-2 py-0.5 rounded bg-yellow-500 hover:bg-yellow-600 text-white"
                                  >
                                    Change
                                  </button>
                                </div>
                              ))
                            ) : (
                              <span className="text-gray-500">No games</span>
                            )}
                          </div>
                        );
                      } catch (e) {
                        return <span className="text-gray-500">Invalid games</span>;
                      }
                    })()}
                  </td>
                  <td className="p-2 border text-left align-top">
                    {/* Escort 1 */}
                    {r.escort_name ? (
                      <div className="mb-1">
                        <p className="font-medium text-xs">{r.escort_name}</p>
                        {r.escort_email && <p className="text-[11px] text-slate-500">{r.escort_email}</p>}
                        <p className="text-[11px] text-gray-600">{r.escort_type}</p>

                        {r.escort_photo && (
                          <button
                            onClick={() => setPreviewImage(getImageUrl(r.escort_photo))}
                            className="text-blue-600 text-[11px] underline"
                          >
                            View Photo
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500">No Escort</p>
                    )}

                    {/* Escort 2 */}
                    {r.escort_name_2 && (
                      <div className="mt-1 pt-1 border-t">
                        <p className="font-medium text-xs">{r.escort_name_2}</p>
                        {r.escort_email_2 && <p className="text-[11px] text-slate-500">{r.escort_email_2}</p>}
                        <p className="text-[11px] text-gray-600">{r.escort_type_2}</p>

                        {r.escort_photo_2 && (
                          <button
                            onClick={() => setPreviewImage(getImageUrl(r.escort_photo_2))}
                            className="text-blue-600 text-[11px] underline"
                          >
                            View Photo
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-2 border">
                    {r.payment_proof ? (
                      <button
                        onClick={() => setPreviewImage(getImageUrl(r.payment_proof))}
                        className="text-blue-600 hover:underline text-xs"
                      >
                        View
                      </button>
                    ) : (
                      "Not Uploaded"
                    )}
                  </td>
                  <td className="p-2 border">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
                        r.status === "Approved"
                          ? "bg-green-100 text-green-700"
                          : r.status === "Rejected"
                          ? "bg-red-100 text-red-600"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {statusLabel(r)}
                    </span>
                  </td>
                  <td className="p-2 border">
                    {(() => {
                      const level = String(r.event_level || "").toLowerCase().trim();
                      const higherLevel = ["state", "national", "international"].includes(level);
                      const isDistrictHigherFlow = roleLower === "district" && higherLevel;
                      const stage = String(r.approval_stage || "").toLowerCase().trim();
                      const forwardedByDistrict = r.status === "Pending" && stage === "district";
                      const statusBusy = updatingStatusId === r.registration_id;

                      return (
                        <div className="flex flex-nowrap justify-start gap-1.5 overflow-x-auto py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                          {!isDistrictHigherFlow ? (
                            <>
                              <button
                                onClick={() => handleStatusChange(r.registration_id, "Approved")}
                                disabled={r.status === "Approved" || statusBusy}
                                className={`px-2 py-1 rounded text-[11px] whitespace-nowrap ${
                                  r.status === "Approved" || statusBusy
                                    ? "bg-green-300 cursor-not-allowed"
                                    : "bg-green-600 hover:bg-green-700 text-white"
                                }`}
                              >
                                {statusBusy ? "Updating..." : "Approve"}
                              </button>
                              <button
                                onClick={() => handleStatusChange(r.registration_id, "Rejected")}
                                disabled={r.status === "Rejected" || statusBusy}
                                className={`px-2 py-1 rounded text-[11px] whitespace-nowrap ${
                                  r.status === "Rejected" || statusBusy
                                    ? "bg-red-300 cursor-not-allowed"
                                    : "bg-red-600 hover:bg-red-700 text-white"
                                }`}
                              >
                                {statusBusy ? "Updating..." : "Reject"}
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleStatusChange(r.registration_id, "Pending")}
                              disabled={statusBusy || r.status === "Approved" || forwardedByDistrict}
                              className={`px-2 py-1 rounded text-[11px] whitespace-nowrap ${
                                statusBusy || r.status === "Approved" || forwardedByDistrict
                                  ? "bg-amber-300 cursor-not-allowed"
                                  : "bg-amber-600 hover:bg-amber-700 text-white"
                              }`}
                            >
                              {statusBusy ? "Updating..." : (forwardedByDistrict ? "Forwarded" : "Forward to State")}
                            </button>
                          )}

                          <button
                            onClick={() => openEscortModal(r)}
                            className="bg-indigo-500 hover:bg-indigo-600 text-white px-2 py-1 rounded text-[11px] whitespace-nowrap"
                          >
                            Change Escort
                          </button>
                          <button
                            onClick={() =>
                              API.downloadAdminSingleId(r.player_id, r.event_id)
                            }
                            disabled={r.status !== "Approved"}
                            className={`px-2 py-1 rounded text-[11px] whitespace-nowrap ${
                              r.status === "Approved"
                                ? "bg-blue-600 hover:bg-blue-700 text-white"
                                : "bg-gray-300 text-gray-600 cursor-not-allowed"
                            }`}
                          >
                            ID Card
                          </button>
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </section>

        {selectedGameReg && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-xl p-6 w-[90%] md:w-[420px] relative shadow-xl">
              <button
                className="absolute top-3 right-3 text-gray-500 hover:text-red-600"
                onClick={() => setSelectedGameReg(null)}
              >
                ✕
              </button>

              <h3 className="text-xl font-semibold mb-3 text-center">
                Change game for {selectedGameReg.player_name}
              </h3>

              <div className="mb-3">
                <label className="block text-sm mb-1">Current Game</label>
                  <input
                  type="text"
                  readOnly
                  value={
                    selectedGameReg.current_game_name ||
                    gameLabel(
                      (Array.isArray(availableGames) ? availableGames : [])
                        .find(g => String(g.id) === String(selectedGameReg.current_game_id))
                    )
                  }
                  className="w-full border p-2 rounded bg-gray-100"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm mb-1">Select New Game</label>
                <SearchableSelect
                  value={newGameForReg}
                  onChange={(e) => setNewGameForReg(e.target.value)}
                  className="w-full border p-2 rounded"
                >
                  <option value="">-- choose game --</option>
                  {availableGames.map((g) => (
                    <option key={g.id} value={g.id}>
                      {gameLabel(g)}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={saveChangedSingleGame}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded"
                >
                  Save
                </button>
                <button
                  onClick={() => setSelectedGameReg(null)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Escort Modal */}
        {selectedEscort && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center px-3"
            onClick={() => setSelectedEscort(null)}   // 👈 click outside closes
          >
            <div
              className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative shadow-xl"
              onClick={(e) => e.stopPropagation()}     // 👈 prevent close on modal click
            >
              {/* Close button (ALWAYS visible) */}
              <button
                className="sticky top-0 float-right m-3 text-gray-500 hover:text-red-600 text-xl font-bold z-10"
                onClick={() => setSelectedEscort(null)}
              >
                ✕
              </button>

              <div className="p-6 pt-2">
                <h2 className="text-xl font-semibold text-center mb-4 text-blue-700">
                  Change Escort / Guide Runner for {selectedEscort.player_name}
                </h2>

                {/* Escort 1 */}
                <h4 className="font-semibold mb-2">Escort / Guide Runner 1</h4>

                <input
                  type="text"
                  name="escort_name"
                  value={escortForm.escort_name}
                  onChange={handleEscortChange}
                  placeholder="Escort or Guide Runner Name"
                  className="border p-2 rounded w-full mb-2"
                />
                <input
                  type="email"
                  name="escort_email"
                  value={escortForm.escort_email || ""}
                  onChange={handleEscortChange}
                  placeholder="Escort or Guide Runner Email"
                  className="border p-2 rounded w-full mb-2"
                />

                <input
                  type="text"
                  name="escort_type"
                  value={escortForm.escort_type}
                  onChange={handleEscortChange}
                  placeholder="Guide Runner / Coach / Parent / Helper"
                  className="border p-2 rounded w-full mb-2"
                />

                {selectedEscort.escort_photo && (
                  <img
                    src={getImageUrl(selectedEscort.escort_photo)}
                    alt="Escort 1"
                    className="w-24 h-24 object-cover rounded border mb-2"
                  />
                )}

                <input
                  type="file"
                  name="escort_photo"
                  accept="image/*"
                  onChange={handleEscortChange}
                  className="border p-2 rounded w-full mb-4"
                />

                <hr className="my-4" />

                {/* Escort 2 */}
                <h4 className="font-semibold mb-2">Escort / Guide Runner 2</h4>

                <input
                  type="text"
                  name="escort_name_2"
                  value={escortForm.escort_name_2}
                  onChange={handleEscortChange}
                  placeholder="Escort or Guide Runner 2 Name"
                  className="border p-2 rounded w-full mb-2"
                />
                <input
                  type="email"
                  name="escort_email_2"
                  value={escortForm.escort_email_2 || ""}
                  onChange={handleEscortChange}
                  placeholder="Escort or Guide Runner 2 Email"
                  className="border p-2 rounded w-full mb-2"
                />

                <input
                  type="text"
                  name="escort_type_2"
                  value={escortForm.escort_type_2}
                  onChange={handleEscortChange}
                  placeholder="Guide Runner / Coach / Parent / Helper"
                  className="border p-2 rounded w-full mb-2"
                />

                {selectedEscort.escort_photo_2 && (
                  <img
                    src={getImageUrl(selectedEscort.escort_photo_2)}
                    alt="Escort 2"
                    className="w-24 h-24 object-cover rounded border mb-2"
                  />
                )}

                <input
                  type="file"
                  name="escort_photo_2"
                  accept="image/*"
                  onChange={handleEscortChange}
                  className="border p-2 rounded w-full mb-6"
                />
                <div className="flex gap-2 mb-4">
                  {/* Remove Escort 1 */}
                  {escortForm.escort_name && (
                    <button
                      type="button"
                      onClick={async () => {
                        const result = await Swal.fire({
                          icon: "warning",
                          title: "Remove Escort 1?",
                          showCancelButton: true,
                          confirmButtonText: "Remove",
                        });
                        if (!result.isConfirmed) return;

                        const fd = new FormData();
                        fd.append("_method", "PUT");
                        fd.append("remove_escort_1", "1");

                        API.post(
                          `/admin/registrations/${selectedEscort.registration_id}/update-escort`,
                          fd
                        ).then(() => {
                          setSelectedEscort(null);
                          loadData();
                        });
                      }}
                      className="flex-1 bg-red-100 text-red-700 py-2 rounded hover:bg-red-200"
                    >
                      Remove Escort 1
                    </button>
                  )}

                  {/* Remove Escort 2 */}
                  {escortForm.escort_name_2 && (
                    <button
                      type="button"
                      onClick={async () => {
                        const result = await Swal.fire({
                          icon: "warning",
                          title: "Remove Escort 2?",
                          showCancelButton: true,
                          confirmButtonText: "Remove",
                        });
                        if (!result.isConfirmed) return;

                        const fd = new FormData();
                        fd.append("_method", "PUT");
                        fd.append("remove_escort_2", "1");

                        API.post(
                          `/admin/registrations/${selectedEscort.registration_id}/update-escort`,
                          fd
                        ).then(() => {
                          setSelectedEscort(null);
                          loadData();
                        });
                      }}
                      className="flex-1 bg-red-100 text-red-700 py-2 rounded hover:bg-red-200"
                    >
                      Remove Escort 2
                    </button>
                  )}
                </div>
                {(escortForm.escort_name || escortForm.escort_name_2) && (
                  <button
                    type="button"
                    onClick={async () => {
                      const result = await Swal.fire({
                        icon: "warning",
                        title: "Remove BOTH escorts?",
                        showCancelButton: true,
                        confirmButtonText: "Remove",
                      });
                      if (!result.isConfirmed) return;

                      const fd = new FormData();
                      fd.append("_method", "PUT");
                      fd.append("remove_escort_1", "1");
                      fd.append("remove_escort_2", "1");

                      API.post(
                        `/admin/registrations/${selectedEscort.registration_id}/update-escort`,
                        fd
                      ).then(() => {
                        setSelectedEscort(null);
                        loadData();
                      });
                    }}
                    className="w-full bg-red-600 text-white py-2 rounded hover:bg-red-700 mb-4"
                  >
                    Remove Both Escorts
                  </button>
                )}
                <button
                  onClick={saveEscortDetails}
                  className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                >
                  Save Escort Details
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {previewImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 transition-opacity"
          onClick={() => setPreviewImage(null)} // click empty space closes
        >
          <div
            className="relative max-w-[90%] max-h-[90%]"
            onClick={(e) => e.stopPropagation()} // prevent close when clicking image
          >
            <img
              src={previewImage}
              alt="Preview"
              className="rounded-lg shadow-xl max-h-[90vh] max-w-[90vw] object-contain"
            />
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-2 right-2 bg-black bg-opacity-60 text-white rounded-full px-3 py-1 hover:bg-opacity-80"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EventRegistrations;
